import { useEffect, useState } from "react";
import TimeLineModal from "./rfq-modals/timeline-modal";
import { fetchRFQProduct, getRfqById, getRfqSummary, installationCharge, updateDeliveryCharge, VATCharge } from "@/store/slices/rfqSlice";
import { useDispatch, useSelector } from "react-redux";
import Toaster from "@/components/Toaster";
import Swal from "sweetalert2";
import useHasPermission from "@/components/useHasPermission";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { MapPinIcon } from "@heroicons/react/24/solid";

export function RfqSummary({ rfq_id, search, sortingOrder }) {
    const dispatch = useDispatch();
    const [deliveryCharge, setDeliveryCharge] = useState(0);
    const { t } = useTranslation(['rfq', 'common']);
    const { t: ct } = useTranslation("common");

    const hasUpdatePermission = useHasPermission(`rfq.update`);
    const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);

    const { rfq_summary, rfq_details } = useSelector((state) => state.rfq);
    const { user } = useSelector((state) => state.user);

    const amountAfterDiscount = Number(rfq_summary?.amount ?? 0) - Number(rfq_summary?.discount ?? 0);
    const vatAmount = Number(rfq_summary?.vat_add ?? 0);

    const [isOpen, setIsOpen] = useState(false);
    const userRole = user?.roles?.[0] || "";
    const isMember = user?.parent_id != null || user?.designation?.toLowerCase() === "member";

    const isAdminApproved = rfq_details?.admin_approval === 'yes';

    const isCustomerApproved = rfq_details?.customer_approval === 'yes';

    const isSalesMemberBlocked = isMember && userRole === "Sales Representative" && rfq_details?.sales_representative_approval === 'yes';
    const isProcurementMemberBlocked = isMember && userRole?.startsWith("Procurement") && rfq_details?.procurement_manager_approval === 'yes';

    // const isAdminRole = userRole === "Administrator" || userRole === "Sub Admin";

    // const isEditingRestricted = isAdminRole
    //     ? isCustomerApproved
    //     : (isAdminApproved || isCustomerApproved || isSalesMemberBlocked || isProcurementMemberBlocked);

    const isProcurementAssigned = rfq_details?.assigned_details?.some((item) => item?.type === "Procurement");
    const isProcurementSentBack = rfq_details?.is_procurement_rejected === 'yes';

    const isSalesManager = !isMember && userRole === "Sales Representative";
    const isSalesManagerBlocked = isSalesManager && isProcurementAssigned && !isProcurementSentBack;

    const isSentToAdmin = rfq_details?.is_send_to_admin != null;
    const isAdminRejected = rfq_details?.admin_approval === 'no';

    const isProcurementManager = !isMember && userRole?.startsWith("Procurement");
    const isProcurementManagerBlocked = isProcurementManager && isSentToAdmin && !isAdminRejected;

    const isAdminRole = userRole === "Administrator" || userRole === "Sub Admin";

    const isEditingRestricted = isAdminRole
        ? isCustomerApproved
        : (
            isAdminApproved ||
            isCustomerApproved ||
            isSalesMemberBlocked ||
            isProcurementMemberBlocked ||
            isSalesManagerBlocked ||
            isProcurementManagerBlocked
        );

    useEffect(() => {
        if (rfq_id) {
            dispatch(getRfqSummary(rfq_id));
        }
    }, [dispatch, rfq_id]);

    useEffect(() => {
        if (rfq_summary?.delivery_charge !== undefined && rfq_summary?.delivery_charge !== null) {
            setDeliveryCharge(Number(rfq_summary.delivery_charge) || 0);
        }
    }, [rfq_summary?.delivery_charge]);

    const isInstallationCharge = (event) => {
        if (!hasUpdatePermission) {
            Toaster.error(ct("noPermission"));
            return;
        }
        if (isEditingRestricted) {
            Toaster.error(t('installation_charge_update_denied'));
            return;
        }
        const value = event.target.checked ? 'yes' : 'no';
        Swal.fire({
            title: ct("areYouSure"),
            text: value === 'yes' ? t("confirm_add_installation_charge") : t("confirm_remove_installation_charge"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("yes"),
            cancelButtonText: t("cancel"),

        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(installationCharge({ type: 'all', rfq_id: rfq_id, is_installation: value })).unwrap().then(() => {
                    // Toaster.success(t("installation_charge_update_success", { status: value === 'yes' ? 'added' : 'removed' }));
                    let newStatus = value === 'yes' ? t('added') : t('removed');
                    Toaster.success(t("installation_charge_update_success", { status: newStatus }));
                    dispatch(getRfqSummary(rfq_id));
                    // dispatch(getRfqById(rfq_id));
                    dispatch(fetchRFQProduct({ rfq_id: rfq_id, search: search }));
                }).catch(() => {
                    Toaster.error(ct("actionFailed"));
                });
            }
        });
    };

    const isVatCharge = (event) => {
        if (!hasUpdatePermission) {
            Toaster.error(ct("noPermission"));
            return;
        }
        if (isEditingRestricted) {
            Toaster.error(t('vat_charge_update_denied'));
            return;
        }
        const value = event.target.checked ? 'yes' : 'no';
        Swal.fire({
            title: ct("areYouSure"),
            text: value === 'yes' ? t("confirm_add_vat_charge") : t("confirm_remove_vat_charge"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("yes"),
            cancelButtonText: t("cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(VATCharge(rfq_id)).unwrap().then(() => {
                    // Toaster.success(t("vat_charge_update_success", { status: value === 'yes' ? 'added' : 'removed' }));
                    let newStatus = value === 'yes' ? t('added') : t('removed');
                    Toaster.success(t("vat_charge_update_success", { status: newStatus }));
                    dispatch(getRfqSummary(rfq_id));
                    // dispatch(getRfqById(rfq_id));
                    dispatch(fetchRFQProduct({ rfq_id: rfq_id, search: search, sort_order: sortingOrder }));
                }).catch(() => {
                    Toaster.error(ct("actionFailed"));
                });
            }
        });
    };

    const handleDeliveryChange = (delivery_charge) => {
        if (!hasUpdatePermission) {
            Toaster.error(ct("noPermission"));
            return;
        }
        if (isEditingRestricted) {
            Toaster.error(t('delivery_charge_update_denied'));
            return;
        }

        const charge = Number(delivery_charge) || 0;
        setDeliveryCharge(charge);

        dispatch(updateDeliveryCharge({ rfq_id, delivery_charge: charge }))
            .unwrap()
            .then(() => {
                Toaster.success(t("deliveryChargeUpdateSuccess"));
                dispatch(getRfqSummary(rfq_id));
                // dispatch(getRfqById(rfq_id));
            })
            .catch(() => {
                Toaster.error(ct("deliveryChargeUpdateFailed"));
            });
    };

    const [tempDelivery, setTempDelivery] = useState(deliveryCharge);

    useEffect(() => {
        setTempDelivery(deliveryCharge);
    }, [deliveryCharge]);

    return (
        <>
            {/* TRIGGER BUTTON (Vertical Sidebar Label) */}
            <div
                className={`fixed right-0 top-1/2 -translate-y-1/2 z-[99] transition-all duration-300 ${isOpen ? 'mr-[350px]' : 'mr-0'}`}
            >
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex flex-col items-center gap-2 bg-black text-white px-2 py-6 rounded-l-xl shadow-xl hover:bg-gray-800 transition-colors"
                >
                    <span className="mb-1">
                        {isOpen ? (
                            <ChevronRightIcon className="h-5 w-5 text-white" />
                        ) : (
                            <ChevronLeftIcon className="h-5 w-5 text-white" />
                        )}
                    </span>
                    <span
                        className="text-sm font-bold tracking-widest"
                        style={{ writingMode: 'vertical-rl' }}
                    >
                        {t("rfq_summary_sidebar_label")}
                    </span>
                </button>
            </div>

            {/* SLIDING DRAWER CONTAINER */}
            <div
                className={`fixed top-0 right-0 h-full w-[350px] bg-white shadow-2xl z-[98] transition-transform duration-300 transform border-l border-gray-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="h-full overflow-y-auto p-6 pt-20">
                    <div className="lg:col-span-1">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">{t("summary_title")}</h2>
                            <svg className="h-6 w-6 text-gray-800 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} onClick={() => {
                                setIsTimelineModalOpen(true);
                            }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>

                        <p className="text-xs text-gray-600 mb-4">{t("prices_in_sar_note")} ({rfq_summary?.currency_code})</p>

                        <div className="space-y-1 mb-6">
                            <p className="text-base text-gray-900 ml-2">{t("total_items_label")}</p>
                            <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                                <span className="flex items-center space-x-2 text-sm font-normal">
                                    <span className="text-gray-800">{t("total_items_label")}</span>
                                </span>
                                <span className="text-sm font-medium text-gray-800">
                                    {rfq_summary?.totalItems}
                                </span>
                            </div>

                            {/* Customer Delivery Address */}
                            <div className="py-4 px-4 rounded-xl bg-gray-300 border border-gray-200 shadow-sm">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-1.5 h-4 bg-black rounded-full" />
                                            <p className="text-xs font-bold uppercase tracking-wider text-gray-700">
                                                {t("delivery_location_label")}
                                            </p>
                                        </div>

                                        <p
                                            className="text-sm font-semibold text-gray-900 leading-relaxed break-words"
                                            title={rfq_summary?.customer_delivery_address}
                                        >
                                            {rfq_summary?.customer_delivery_address || (
                                                <span className="text-gray-400 italic font-normal">{t("no_address_provided")}</span>
                                            )}
                                        </p>
                                    </div>

                                    {rfq_summary?.latitude && rfq_summary?.longitude && (
                                        <div className="shrink-0 flex flex-col items-center gap-1">
                                            <a
                                                href={`https://www.google.com/maps?q=${rfq_summary.latitude},${rfq_summary.longitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={ct("openInGoogleMaps")}
                                                className="flex items-center justify-center h-10 w-10 rounded-xl bg-black text-white hover:bg-gray-800 hover:shadow-md active:scale-95 transition-all duration-200"
                                            >
                                                <MapPinIcon className="h-5 w-5" />
                                            </a>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase">{t("map_button")}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Delivery Charge Block */}
                            <div className="group flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300">
                                <span className="flex items-center space-x-2 text-sm font-normal">
                                    <input
                                        type="checkbox"
                                        checked={Number(deliveryCharge) > 0}
                                        onChange={() => {
                                            if (Number(deliveryCharge) > 0) {
                                                handleDeliveryChange(0);
                                            }
                                        }}
                                        disabled={isEditingRestricted}
                                        className="form-checkbox h-4 w-4 bg-gray-900 border-gray-900 accent-[#000000] rounded-sm cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                    <span className="text-gray-800">{t("delivery_charge_label")}</span>
                                </span>
                                {(hasUpdatePermission && !isEditingRestricted) ? (
                                    <div className="relative flex items-center">
                                        <span className="text-sm font-medium text-gray-800 group-hover:hidden">{Number(deliveryCharge)}</span>
                                        <div className="hidden group-hover:flex items-center gap-2">
                                            <input type="number" value={tempDelivery} onChange={(e) => setTempDelivery(e.target.value)} className="w-20 text-sm px-2 py-1 rounded border border-gray-400 text-right bg-white" />
                                            <button onClick={() => { const newVal = Number(tempDelivery) || 0; if (newVal === Number(deliveryCharge)) return; handleDeliveryChange(newVal); }} className="text-xs bg-black text-white px-2 py-1 rounded hover:bg-gray-800">{ct("save")}</button>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-sm font-medium text-gray-800">{Number(deliveryCharge).toFixed(2)}</span>
                                )}
                            </div>

                            {/* Installation Charge Block */}
                            <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                                <span className="flex items-center space-x-2 text-sm font-normal">
                                    <input
                                        type="checkbox"
                                        checked={rfq_summary?.installation_no_count == 0}
                                        disabled={isEditingRestricted}
                                        className="form-checkbox h-4 w-4 bg-gray-900 text-gray-900 border-gray-900 accent-[#000000] rounded-sm focus:ring-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={(e) => isInstallationCharge(e)}
                                    />
                                    <span className="text-gray-800">{t("installation_charge_label")}</span>
                                </span>
                                <span className="text-sm font-medium text-gray-800">{rfq_summary?.installation_add}</span>
                            </div>
                        </div>

                        {/* Sub Total Block */}
                        <div className="space-y-1">
                            <h3 className="text-lg font-semibold text-gray-900">{t("sub_total_title")}</h3>
                            <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                                <span className="text-gray-800 text-sm">{t("amount_before_discount_label")}</span>
                                <span className="text-sm font-medium text-gray-800">{rfq_summary?.amount}</span>
                            </div>
                            <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                                <span className="text-gray-800 text-sm">{t("discount_label")}</span>
                                <span className="text-sm font-medium text-gray-800">{rfq_summary?.discount}</span>
                            </div>
                            <div className='flex justify-between items-center py-2 px-3 rounded-lg bg-gray-300'>
                                <span className="text-gray-800 text-sm">{t("amount_after_discount_label")}</span>
                                <span className="text-sm font-medium text-gray-800">
                                    {((rfq_summary?.amount - rfq_summary?.discount))}
                                </span>
                            </div>

                            {/* VAT Block */}
                            <div className='flex flex-col gap-1 py-2 px-3 rounded-lg bg-gray-300'>
                                <div className="flex justify-between items-center w-full">
                                    <span className="flex items-center space-x-2 text-sm font-normal">
                                        <input
                                            type="checkbox"
                                            checked={rfq_summary?.is_vat_charge === 'yes'}
                                            disabled={isEditingRestricted}
                                            className="form-checkbox h-4 w-4 bg-gray-900 text-gray-900 border-gray-900 accent-[#000000] rounded-sm focus:ring-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                            onClick={(e) => isVatCharge(e)}
                                        />
                                        <span className="text-gray-800">{t("total_vat_label")} ({rfq_summary?.currency_code})</span>
                                    </span>
                                    <span className="text-sm font-medium text-gray-800">{rfq_summary?.is_vat_charge === "yes" ? vatAmount : "0.00"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-3 pt-4 border-t border-gray-100 text-right">
                            <p className="text-base text-gray-700">{t("payable_amount_label")} ({rfq_summary?.currency_code})</p>
                            <p className="text-4xl font-extrabold text-gray-900 mt-1">{rfq_summary?.payableAmount}</p>
                        </div>
                    </div>
                </div>
            </div>

            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/20 z-[97]"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {isTimelineModalOpen && (
                <TimeLineModal
                    isOpen={isTimelineModalOpen}
                    onClose={() => setIsTimelineModalOpen(false)}
                    rfqId={rfq_id}
                />
            )}
        </>
    );
}

export default RfqSummary;